import apiClient from '../client';
import type { AlertResponse } from '@/types/alert.types';

// Dashboard-specific types

export interface DashboardSummary {
  totalPatients: number;
  patientsHighRisk: number;
  patientsCriticalRisk: number;
  patientsInCareLines: number;
  openDemands: number;
  urgentDemands: number;
  unassignedDemands: number;
  resolvedToday: number;
  pendingTasks: number;
  overdueTasks: number;
  myPendingTasks: number;
  pendingQuestionnaires: number;
  alertsToReview: number;
  answeredToday: number;
  unreadMessages: number;
  urgentMessages: number;
  generatedAt: string;
}

// API response format from backend
interface RiskDistributionApiResponse {
  lowRisk: number;
  moderateRisk: number;
  highRisk: number;
  criticalRisk: number;
  notAssessed: number;
  total: number;
  lowRiskPercent: number;
  moderateRiskPercent: number;
  highRiskPercent: number;
  criticalRiskPercent: number;
  notAssessedPercent: number;
}

// Frontend format
export interface RiskDistribution {
  low: number;
  moderate: number;
  high: number;
  critical: number;
  notAssessed: number;
  total: number;
}

// API response format from backend for care line distribution
interface CareLineDistributionApiResponse {
  totalEnrolled: number;
  careLines: Array<{
    id: string;
    code: string;
    name: string;
    activePatients: number;
  }>;
}

// Frontend format
export interface CareLineDistribution {
  distributions: Array<{
    careLineId: string;
    careLineName: string;
    careLineCode: string;
    patientCount: number;
  }>;
  total: number;
}

export interface PatientAtRiskApi {
  patientId: string;
  patientName: string;
  age: number;
  riskLevel: 'HIGH' | 'CRITICAL';
  riskColor: string;
  overallScore: number;
  mainRiskFactors: string[];
  activeCareLines: string[];
  openDemands: number;
  lastAssessmentAt: string | null;
  lastContactAt: string | null;
  expiredAt: string | null;
}

export interface PatientAtRisk {
  id: string;
  name: string;
  age: number;
  riskLevel: 'HIGH' | 'CRITICAL';
  riskScore: number;
  mainProblems: string[];
  lastAssessmentAt: string | null;
  expiredAt?: string | null;
}

export interface PatientsAtRiskApiResponse {
  totalHighRisk: number;
  totalCritical: number;
  totalExpiredAssessments: number;
  patients: PatientAtRiskApi[];
  expiredAssessments: PatientAtRiskApi[];
}

export interface PatientsAtRiskResponse {
  patients: PatientAtRisk[];
  expiredAssessments: PatientAtRisk[];
  totalHighRisk: number;
  totalCritical: number;
  totalExpiredAssessments: number;
}

// API response format from backend for alerts
interface AlertsSummaryApiResponse {
  totalAlerts: number;
  criticalAlerts: number;
  unreviewedAlerts: number;
  recentAlerts: Array<{
    id: string;
    type: string;
    source: string;
    message: string;
    severity: string;
    reviewed: boolean;
    patientId: string;
    patientName: string;
    questionnaireResponseId: string;
    questionnaireName: string;
    score: number;
    createdAt: string;
  }>;
}

/**
 * Resumo de alertas para o dashboard
 * Usa AlertResponse do modulo de alertas
 */
export interface DashboardAlertsSummary {
  alerts: AlertResponse[];
  totalUnread: number;
  criticalCount: number;
  highCount: number;
}

// API response format from backend for recent activities
interface RecentActivityApiResponse {
  activities: Array<{
    id: string;
    type: string;
    description: string;
    icon: string;
    color: string;
    patientId?: string;
    patientName?: string;
    userId?: string;
    userName?: string;
    occurredAt: string;
  }>;
}

// Frontend format
export interface RecentActivity {
  id: string;
  type: string;
  title: string;
  description: string;
  patientId?: string;
  patientName?: string;
  userId: string;
  userName: string;
  createdAt: string;
}

export interface RecentActivitiesResponse {
  activities: RecentActivity[];
}

// Consolidated dashboard API response from backend
interface DashboardAllApiResponse {
  summary: DashboardSummary;
  riskDistribution: RiskDistributionApiResponse;
  careLineDistribution: CareLineDistributionApiResponse;
  patientsAtRisk: PatientsAtRiskApiResponse;
  alerts: AlertsSummaryApiResponse;
  recentActivities: RecentActivityApiResponse;
  generatedAt: string;
  cached: boolean;
}

// Consolidated dashboard response for frontend
export interface DashboardAllResponse {
  summary: DashboardSummary;
  riskDistribution: RiskDistribution;
  careLineDistribution: CareLineDistribution;
  patientsAtRisk: PatientsAtRiskResponse;
  alertsSummary: DashboardAlertsSummary;
  recentActivities: RecentActivitiesResponse;
  generatedAt: string;
  cached: boolean;
}

export const dashboardApi = {
  /**
   * Fetches all dashboard data in a single request (optimized).
   * Reduces 6 HTTP requests to 1, improving performance.
   */
  getAll: async (): Promise<DashboardAllResponse> => {
    const response = await apiClient.get<DashboardAllApiResponse>('/v1/dashboard/all');
    const data = response.data;

    // Transform API response to frontend format
    return {
      summary: data.summary,
      riskDistribution: {
        low: data.riskDistribution.lowRisk,
        moderate: data.riskDistribution.moderateRisk,
        high: data.riskDistribution.highRisk,
        critical: data.riskDistribution.criticalRisk,
        notAssessed: data.riskDistribution.notAssessed,
        total: data.riskDistribution.total,
      },
      careLineDistribution: {
        distributions: (data.careLineDistribution.careLines || []).map((cl) => ({
          careLineId: cl.id,
          careLineName: cl.name,
          careLineCode: cl.code,
          patientCount: cl.activePatients,
        })),
        total: data.careLineDistribution.totalEnrolled,
      },
      patientsAtRisk: {
        patients: data.patientsAtRisk.patients.map((p) => ({
          id: p.patientId,
          name: p.patientName,
          age: p.age,
          riskLevel: p.riskLevel,
          riskScore: p.overallScore,
          mainProblems: p.mainRiskFactors || [],
          lastAssessmentAt: p.lastAssessmentAt,
        })),
        expiredAssessments: (data.patientsAtRisk.expiredAssessments || []).map((p) => ({
          id: p.patientId,
          name: p.patientName,
          age: p.age,
          riskLevel: p.riskLevel,
          riskScore: p.overallScore,
          mainProblems: p.mainRiskFactors || [],
          lastAssessmentAt: p.lastAssessmentAt,
          expiredAt: p.expiredAt,
        })),
        totalHighRisk: data.patientsAtRisk.totalHighRisk,
        totalCritical: data.patientsAtRisk.totalCritical,
        totalExpiredAssessments: data.patientsAtRisk.totalExpiredAssessments || 0,
      },
      alertsSummary: {
        alerts: (data.alerts.recentAlerts || []).map((a) => ({
          id: a.id,
          patientId: a.patientId,
          patientName: a.patientName,
          type: 'QUESTIONNAIRE_ALERT' as never,
          typeLabel: a.questionnaireName || 'Alerta',
          severity: (a.severity || 'MODERATE') as never,
          severityLabel: a.severity || 'Moderado',
          severityColor: a.severity === 'CRITICAL' ? '#a855f7' : a.severity === 'HIGH' ? '#ef4444' : '#eab308',
          title: `Alerta: ${a.questionnaireName || a.source}`,
          message: a.message || `Score: ${a.score}`,
          sourceType: 'QUESTIONNAIRE',
          sourceId: a.questionnaireResponseId,
          status: a.reviewed ? 'RESOLVED' as never : 'PENDING' as never,
          createdAt: a.createdAt,
          urgent: a.severity === 'CRITICAL' || a.severity === 'HIGH',
        })),
        totalUnread: data.alerts.unreviewedAlerts,
        criticalCount: data.alerts.criticalAlerts,
        highCount: data.alerts.totalAlerts - data.alerts.criticalAlerts,
      },
      recentActivities: {
        activities: (data.recentActivities.activities || []).map((a) => ({
          id: a.id,
          type: a.type,
          title: a.description.split(':')[0] || a.type,
          description: a.description,
          patientId: a.patientId,
          patientName: a.patientName,
          userId: a.userId || '',
          userName: a.userName || 'Sistema',
          createdAt: a.occurredAt,
        })),
      },
      generatedAt: data.generatedAt,
      cached: data.cached,
    };
  },

  getSummary: async (): Promise<DashboardSummary> => {
    const response = await apiClient.get<DashboardSummary>('/v1/dashboard/summary');
    return response.data;
  },

  getRiskDistribution: async (): Promise<RiskDistribution> => {
    const response = await apiClient.get<RiskDistributionApiResponse>('/v1/dashboard/risk-distribution');
    const apiData = response.data;
    // Transform API response to frontend format
    return {
      low: apiData.lowRisk,
      moderate: apiData.moderateRisk,
      high: apiData.highRisk,
      critical: apiData.criticalRisk,
      notAssessed: apiData.notAssessed,
      total: apiData.total,
    };
  },

  getCareLineDistribution: async (): Promise<CareLineDistribution> => {
    const response = await apiClient.get<CareLineDistributionApiResponse>('/v1/dashboard/careline-distribution');
    const apiData = response.data;
    // Transform API response to frontend format
    return {
      distributions: (apiData.careLines || []).map((cl) => ({
        careLineId: cl.id,
        careLineName: cl.name,
        careLineCode: cl.code,
        patientCount: cl.activePatients,
      })),
      total: apiData.totalEnrolled,
    };
  },

  getPatientsAtRisk: async (limit = 10): Promise<PatientsAtRiskResponse> => {
    const response = await apiClient.get<PatientsAtRiskApiResponse>('/v1/dashboard/patients-at-risk', {
      params: { limit },
    });
    // Transform API response to frontend format
    const apiData = response.data;
    return {
      patients: apiData.patients.map((p) => ({
        id: p.patientId,
        name: p.patientName,
        age: p.age,
        riskLevel: p.riskLevel,
        riskScore: p.overallScore,
        mainProblems: p.mainRiskFactors || [],
        lastAssessmentAt: p.lastAssessmentAt,
      })),
      expiredAssessments: (apiData.expiredAssessments || []).map((p) => ({
        id: p.patientId,
        name: p.patientName,
        age: p.age,
        riskLevel: p.riskLevel,
        riskScore: p.overallScore,
        mainProblems: p.mainRiskFactors || [],
        lastAssessmentAt: p.lastAssessmentAt,
        expiredAt: p.expiredAt,
      })),
      totalHighRisk: apiData.totalHighRisk,
      totalCritical: apiData.totalCritical,
      totalExpiredAssessments: apiData.totalExpiredAssessments || 0,
    };
  },

  getAlertsSummary: async (limit = 10): Promise<DashboardAlertsSummary> => {
    const response = await apiClient.get<AlertsSummaryApiResponse>('/v1/dashboard/alerts', {
      params: { limit },
    });
    const apiData = response.data;
    // Transform API response to frontend format
    const alerts: AlertResponse[] = (apiData.recentAlerts || []).map((a) => ({
      id: a.id,
      patientId: a.patientId,
      patientName: a.patientName,
      type: 'QUESTIONNAIRE_ALERT' as never, // Maps to AlertType
      typeLabel: a.questionnaireName || 'Alerta',
      severity: (a.severity || 'MODERATE') as never, // Maps to RiskLevel
      severityLabel: a.severity || 'Moderado',
      severityColor: a.severity === 'CRITICAL' ? '#a855f7' : a.severity === 'HIGH' ? '#ef4444' : '#eab308',
      title: `Alerta: ${a.questionnaireName || a.source}`,
      message: a.message || `Score: ${a.score}`,
      sourceType: 'QUESTIONNAIRE',
      sourceId: a.questionnaireResponseId,
      status: a.reviewed ? 'RESOLVED' as never : 'PENDING' as never,
      createdAt: a.createdAt,
      urgent: a.severity === 'CRITICAL' || a.severity === 'HIGH',
    }));

    return {
      alerts,
      totalUnread: apiData.unreviewedAlerts,
      criticalCount: apiData.criticalAlerts,
      highCount: apiData.totalAlerts - apiData.criticalAlerts,
    };
  },

  getRecentActivities: async (limit = 20): Promise<RecentActivitiesResponse> => {
    const response = await apiClient.get<RecentActivityApiResponse>('/v1/dashboard/recent-activities', {
      params: { limit },
    });
    const apiData = response.data;
    // Transform API response to frontend format
    return {
      activities: (apiData.activities || []).map((a) => ({
        id: a.id,
        type: a.type,
        title: a.description.split(':')[0] || a.type,
        description: a.description,
        patientId: a.patientId,
        patientName: a.patientName,
        userId: a.userId || '',
        userName: a.userName || 'Sistema',
        createdAt: a.occurredAt,
      })),
    };
  },
};
